import React from 'react';

import { Button, Stack } from '@mui/material';
import { DateTime } from 'luxon';

import { INTEGRATION_USAGE_LOG_FILTER_IDS } from 'src/constants/integrationUsageLog';
import { useFilterStorage } from 'src/hooks/common';

type Shortcut = {
  label: string;
  getRange: (now: DateTime) => { from: DateTime; to: DateTime };
};

const SHORTCUTS: Shortcut[] = [
  {
    label: 'Today',
    getRange: now => ({ from: now.startOf('day'), to: now.endOf('day') }),
  },
  {
    label: 'Yesterday',
    getRange: now => ({
      from: now.minus({ days: 1 }).startOf('day'),
      to: now.minus({ days: 1 }).endOf('day'),
    }),
  },
  {
    label: 'This week',
    getRange: now => ({ from: now.startOf('week'), to: now.endOf('day') }),
  },
  {
    label: 'Last 7 days',
    getRange: now => ({ from: now.minus({ days: 6 }).startOf('day'), to: now.endOf('day') }),
  },
  {
    label: 'Month to date',
    getRange: now => ({ from: now.startOf('month'), to: now.endOf('day') }),
  },
  {
    label: 'Last 30 days',
    getRange: now => ({ from: now.minus({ days: 29 }).startOf('day'), to: now.endOf('day') }),
  },
  {
    label: 'Year to date',
    getRange: now => ({ from: now.startOf('year'), to: now.endOf('day') }),
  },
];

const parseIso = (value: unknown): DateTime | null => {
  if (typeof value !== 'string') return null;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt : null;
};

export const DateRangeShortcuts: React.FC = () => {
  const { filter, setFilter, pagination } = useFilterStorage();

  const currentFrom = parseIso(filter[INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtFrom]);
  const currentTo = parseIso(filter[INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtTo]);

  const now = DateTime.now();
  const activeShortcut =
    currentFrom && currentTo
      ? (SHORTCUTS.find(shortcut => {
          const range = shortcut.getRange(now);
          return (
            range.from.toISODate() === currentFrom.toISODate() &&
            range.to.toISODate() === currentTo.toISODate()
          );
        }) ?? null)
      : null;

  const handleSelect = React.useCallback(
    (shortcut: Shortcut) => {
      const { from, to } = shortcut.getRange(DateTime.now());
      setFilter({
        ...filter,
        [INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtFrom]: from.toISO(),
        [INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtTo]: to.toISO(),
      });
      pagination.resetPage();
    },
    [filter, pagination, setFilter],
  );

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {SHORTCUTS.map(shortcut => {
        const selected = shortcut.label === activeShortcut?.label;
        return (
          <Button
            key={shortcut.label}
            size="small"
            variant={selected ? 'contained' : 'outlined'}
            onClick={() => handleSelect(shortcut)}
            sx={{ borderRadius: '10px' }}
          >
            {shortcut.label}
          </Button>
        );
      })}
    </Stack>
  );
};
